import React, { useEffect, useMemo, useState, useCallback, ReactNode } from 'react';
import { lightTheme, darkTheme, THEME_NAME_DARK } from '@stylesheets/theme';
import { ThemeCtx } from './ThemeCtx';
import type { ThemePreference } from './ThemeCtx';

export type { ThemePreference } from './ThemeCtx';

const THEME_PREFERENCE_KEY = 'aibuild_theme_preference';

export const AppThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Detect system preference
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    if (typeof globalThis === 'undefined' || !globalThis.window) return 'light';
    return globalThis.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Read stored preference
  const [preference, setPreference] = useState<ThemePreference>(() => {
    if (typeof globalThis === 'undefined' || !globalThis.window) return 'SYSTEM';
    const stored = localStorage.getItem(THEME_PREFERENCE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'SYSTEM') return stored;
    return 'SYSTEM';
  });

  // Listen for system theme changes
  useEffect(() => {
    if (typeof globalThis === 'undefined' || !globalThis.window) return;
    const mq = globalThis.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Resolve the active theme name
  const resolvedThemeName = useMemo<'light' | 'dark'>(
    () => (preference === 'SYSTEM' ? systemTheme : preference),
    [preference, systemTheme]
  );

  const theme = useMemo(
    () => (resolvedThemeName === THEME_NAME_DARK ? darkTheme : lightTheme),
    [resolvedThemeName]
  );

  const setThemePreference = useCallback((pref: ThemePreference) => {
    setPreference(pref);
    localStorage.setItem(THEME_PREFERENCE_KEY, pref);
  }, []);

  const value = useMemo(
    () => ({ theme, themeName: resolvedThemeName, themePreference: preference, setThemePreference }),
    [theme, resolvedThemeName, preference, setThemePreference]
  );

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
};
